export interface GuestSession {
  token: string;
  expiresAt: string;
}

const STORAGE_KEY = 'op.guestSession';

/**
 * sessionStorage rather than localStorage: the session is scoped to one order lookup in one tab, and
 * a customer looking up a second order in a second tab must not inherit the first tab's token.
 * The in-memory fallback keeps this working under SSR and in private modes that reject storage.
 */
let memorySession: GuestSession | null = null;

const storage = (): Storage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const setGuestSession = (session: GuestSession | null | undefined): void => {
  if (!session?.token) return;

  memorySession = session;
  storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
};

export const getGuestSession = (): GuestSession | null => {
  if (memorySession) return memorySession;

  const raw = storage()?.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    memorySession = JSON.parse(raw) as GuestSession;
  } catch {
    // A malformed entry is treated as no session rather than as a hard failure — the customer just
    // gets sent back through lookup, which mints a clean one.
    clearGuestSession();
  }

  return memorySession;
};

export const getGuestToken = (): string | null => getGuestSession()?.token ?? null;

export const clearGuestSession = (): void => {
  memorySession = null;
  storage()?.removeItem(STORAGE_KEY);
};

/**
 * The app registers what should happen when a session dies mid-flow — Pulse deliberately does not
 * know Concierge's routes, and a library that navigates on its own is impossible to test or reuse.
 */
let expiredHandler: (() => void) | null = null;

export const setGuestSessionExpiredHandler = (handler: (() => void) | null): void => {
  expiredHandler = handler;
};

/**
 * Distinct from clearGuestSession: this is the "it expired while they were working" path, and it is
 * the only one that should send the customer anywhere. Clearing a corrupted entry must stay silent.
 */
export const expireGuestSession = (): void => {
  clearGuestSession();
  expiredHandler?.();
};
